import { useState, Suspense, forwardRef, type ReactElement } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { useSubmitPageFilters } from '@/catalog-apps/components/AppPage2/Filter/useSubmitPageFilters'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Button } from '@/components/shadcn/button'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { CATALOG_BASE_PATH } from '@/const'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { useCatalogComponentRuntime } from '../shared/CatalogComponentRuntimeContext'
import { SheetContentAction } from '../DataTableV2/components/SheetContentAction'
import { DataTableV2DataRow } from '../DataTableV2/types'
import { TemplateActivityLog } from '../DataTableComponent/RowActions/TemplateActivityLog'
import { UploadFilesActionPanel } from '../shared/UploadFilesActionPanel'
import { CollaborationContent } from '../DataTableV2/components/CollaborationMenuItemV2/CollaborationSheetContent'
import { FormComponentLoading } from '../FormComponent'
import {
  ListCardAction,
  ListCardCollaborationAction,
  ListCardUploadFilesAction,
  ListCardUploadTemplateAction,
} from './types'
import { cn } from '@/lib/utils'

type ButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost' | 'link'
type ButtonSize = 'sm' | 'icon-xs'
type ActionAppearance = 'button' | 'link' | 'icon'

function getButtonVariant(action: ListCardAction): 'default' | 'secondary' | 'outline' {
  if (action.variant === 'primary') return 'default'
  if (action.variant === 'secondary') return 'secondary'
  return 'outline'
}

function getActionButtonStyles(appearance: ActionAppearance, action: ListCardAction) {
  if (appearance === 'icon') {
    return {
      buttonVariant: 'ghost' as ButtonVariant,
      buttonClassName: 'size-6 shrink-0',
      buttonSize: 'icon-xs' as ButtonSize,
    }
  }

  return {
    buttonVariant: (appearance === 'link' ? 'link' : getButtonVariant(action)) as ButtonVariant,
    buttonClassName: cn(
      appearance === 'link'
        ? 'h-auto w-auto justify-center border-0 bg-transparent p-0 text-statusblue-600 underline-offset-2 hover:bg-transparent hover:underline'
        : 'w-full justify-center'
    ),
    buttonSize: 'sm' as ButtonSize,
  }
}

function wrapIconTooltip(appearance: ActionAppearance, action: ListCardAction, trigger: ReactElement) {
  if (appearance !== 'icon') return trigger
  return (
    <TooltipButton tooltip={action.label} disabled={action.isDisabled}>
      {trigger}
    </TooltipButton>
  )
}

const ActionButton = forwardRef<
  HTMLButtonElement,
  {
    action: ListCardAction
    appearance: ActionAppearance
    buttonVariant: ButtonVariant
    buttonClassName: string
    buttonSize: ButtonSize
    onClick?: () => void
  }
>(function ActionButton({ action, appearance, buttonVariant, buttonClassName, buttonSize, onClick }, ref) {
  const isIcon = appearance === 'icon'

  return (
    <Button
      ref={ref}
      type="button"
      variant={buttonVariant}
      size={buttonSize}
      className={buttonClassName}
      disabled={action.isDisabled}
      onClick={onClick}
    >
      {action.icon && (
        <DynamicIcon iconName={action.icon} lazyLoad={false} className={isIcon ? 'size-4' : 'mr-2 size-4'} />
      )}
      {isIcon ? <span className="sr-only">{action.label}</span> : action.label}
    </Button>
  )
})

function SubmitListCardAction({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: Extract<ListCardAction, { type: 'submit' }>
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const { handlerId, id: actionId } = action
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm } = useSubmitForm(handlerId, [...tileFilterSelections, { actionId }], actionId)

  return wrapIconTooltip(
    appearance,
    action,
    <ActionButton
      action={action}
      appearance={appearance}
      buttonVariant={buttonVariant}
      buttonClassName={buttonClassName}
      buttonSize={buttonSize}
      onClick={() => submitForm(action.submitData)}
    />
  )
}

function SubmitListCardActionWithConfirmation({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: Extract<ListCardAction, { type: 'submit' }>
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { handlerId, id: actionId, confirmationMessage, label } = action
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm, isPending: isSubmitting } = useSubmitForm(
    handlerId,
    [...tileFilterSelections, { actionId }],
    actionId,
    { onSuccess: () => setIsOpen(false) }
  )

  return (
    <ConfirmDialog
      trigger={
        <ActionButton
          action={action}
          appearance={appearance}
          buttonVariant={buttonVariant}
          buttonClassName={buttonClassName}
          buttonSize={buttonSize}
        />
      }
      triggerTooltip={appearance === 'icon' ? label : undefined}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      title={label}
      description={confirmationMessage}
      onConfirm={() => submitForm(action.submitData)}
      closeOnConfirm={false}
      isSubmitting={isSubmitting}
    />
  )
}

function NavigateListCardAction({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: Extract<ListCardAction, { type: 'navigate' }>
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const { submitFilters } = useSubmitPageFilters()
  const [searchParams] = useSearchParams()
  const { allowRouteNavigation = true } = useCatalogComponentRuntime()

  const button = wrapIconTooltip(
    appearance,
    action,
    <ActionButton
      action={action}
      appearance={appearance}
      buttonVariant={buttonVariant}
      buttonClassName={buttonClassName}
      buttonSize={buttonSize}
    />
  )

  if (!allowRouteNavigation) {
    return button
  }

  return (
    <Link
      className="text-foreground"
      to={`${CATALOG_BASE_PATH}${action.pageId}?${searchParams.toString()}`}
      onClick={() => {
        if (action.pageFilters) {
          submitFilters(action.pageFilters)
        }
      }}
    >
      {button}
    </Link>
  )
}

function ListCardUploadTemplateActionButton({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: ListCardUploadTemplateAction
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const [sheetOpen, setSheetOpen] = useState(false)

  return (
    <SheetWithFormComponent
      title="Upload template"
      description="Upload a template for the entity."
      trigger={
        <ActionButton
          action={action}
          appearance={appearance}
          buttonVariant={buttonVariant}
          buttonClassName={buttonClassName}
          buttonSize={buttonSize}
        />
      }
      triggerTooltip={appearance === 'icon' ? action.label : undefined}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <TemplateActivityLog entityGuid={action.entityGUID} templateKey={action.templateKey} />
    </SheetWithFormComponent>
  )
}

function ListCardUploadFilesActionButton({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: ListCardUploadFilesAction
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const uploadNamespace = action.namespace.trim()
  return (
    <UploadFilesActionPanel
      action={action}
      title={action.label}
      uploadNamespace={uploadNamespace}
      permissions={action.permissions}
      trigger={(onOpen) =>
        wrapIconTooltip(
          appearance,
          action,
          <ActionButton
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
            onClick={onOpen}
          />
        )
      }
    />
  )
}

function ListCardCollaborationActionButton({
  action,
  appearance,
  buttonVariant,
  buttonClassName,
  buttonSize,
}: {
  action: ListCardCollaborationAction
  appearance: ActionAppearance
  buttonVariant: ButtonVariant
  buttonClassName: string
  buttonSize: ButtonSize
}) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { appId, workspaceId } = useAppContext()

  if (!appId || !workspaceId) return null

  return (
    <SheetWithFormComponent
      title={action.label}
      trigger={
        <ActionButton
          action={action}
          appearance={appearance}
          buttonVariant={buttonVariant}
          buttonClassName={buttonClassName}
          buttonSize={buttonSize}
        />
      }
      triggerTooltip={appearance === 'icon' ? action.label : undefined}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <QueryErrorBoundary errorMessage="Failed to fetch channel thread details">
        <Suspense fallback={<FormComponentLoading />}>
          <CollaborationContent
            channelKey={action.channelKey}
            threadKey={action.threadKey}
            applicationId={appId}
            workspaceId={workspaceId}
          />
        </Suspense>
      </QueryErrorBoundary>
    </SheetWithFormComponent>
  )
}

export function ListCardActionButton({
  action,
  className,
  appearance = 'button',
}: {
  action: ListCardAction
  className?: string
  appearance?: ActionAppearance
}) {
  if (!action.isVisible) return null

  const wrapperClass = cn(appearance === 'button' ? 'w-full' : 'w-auto shrink-0', className)
  const { buttonVariant, buttonClassName, buttonSize } = getActionButtonStyles(appearance, action)

  switch (action.type) {
    case 'submit':
      return (
        <div className={wrapperClass}>
          {action.requireConfirmation ? (
            <SubmitListCardActionWithConfirmation
              action={action}
              appearance={appearance}
              buttonVariant={buttonVariant}
              buttonClassName={buttonClassName}
              buttonSize={buttonSize}
            />
          ) : (
            <SubmitListCardAction
              action={action}
              appearance={appearance}
              buttonVariant={buttonVariant}
              buttonClassName={buttonClassName}
              buttonSize={buttonSize}
            />
          )}
        </div>
      )
    case 'navigate':
      return (
        <div className={wrapperClass}>
          <NavigateListCardAction
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
          />
        </div>
      )
    case 'sheet-content': {
      const trigger = wrapIconTooltip(
        appearance,
        action,
        <SheetTrigger asChild>
          <ActionButton
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
          />
        </SheetTrigger>
      )
      return (
        <div className={wrapperClass}>
          <SheetContentAction
            action={action}
            trigger={trigger}
            formData={action.sheetData as DataTableV2DataRow | undefined}
            isDisabled={action.isDisabled}
          />
        </div>
      )
    }
    case 'upload-template':
      return (
        <div className={wrapperClass}>
          <ListCardUploadTemplateActionButton
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
          />
        </div>
      )
    case 'upload-files':
      return (
        <div className={wrapperClass}>
          <ListCardUploadFilesActionButton
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
          />
        </div>
      )
    case 'collaboration':
      return (
        <div className={wrapperClass}>
          <ListCardCollaborationActionButton
            action={action}
            appearance={appearance}
            buttonVariant={buttonVariant}
            buttonClassName={buttonClassName}
            buttonSize={buttonSize}
          />
        </div>
      )
    default:
      return null
  }
}
